// ============================================================================
// CostPrintSheet — Kunden-Druckblatt "PV-Richtwert-Kalkulation"
// ----------------------------------------------------------------------------
// Rendert ein druckoptimiertes A4-Blatt als Fixed-Overlay.
// @media print: alles außer dem Overlay ausgeblendet (visibility-Trick),
// Druckknopf + Schließen-Button nicht druckbar.
// Sicherheitsinvarianten: canApprove: false, requiresHumanReview: true —
// strukturell gesetzt in der Engine, wird hier NIE überschrieben.
// ============================================================================
import { PRINT_DISCLAIMER } from "./print-sheet.js";

// Minimale Typ-Spiegel (kein shared-import, um Vite-Abhängigkeiten zu vermeiden)
interface Position { key: string; label: string; ekEur: number | null; vkEur: number }
interface CostOutput {
  quantities: { moduleCount: number; labourHours: number; railMeters: number; dcSurgeCount: number };
  materialPositions: Position[];
  labourPosition: Position;
  flatPositions: Position[];
  materialVkEur: number;
  labourVkEur: number;
  subtotalNetEur: number;
  surchargeEur: number;
  totalNetEur: number;
  vatEur: number;
  totalGrossEur: number;
  pricePerKwpNetEur: number;
  canApprove: false;
  requiresHumanReview: true;
}
interface EngineEnvelope {
  status: string;
  data: CostOutput | null;
  warnings: Array<{ code: string; message: string; severity: string }>;
  errors: Array<{ code: string; message: string }>;
}

export interface CostPrintSheetProps {
  result: EngineEnvelope;
  kwp: number;
  moduleWp: number;
  ukSystem: string;
  projectName?: string;
  customerName?: string;
  onClose: () => void;
}

const eur = (n: number) =>
  n.toLocaleString("de-AT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

// ELMETIC-Markenfarben (aus Logo): schwarzer Schriftzug + roter/oranger Akzent.
const BRAND_BLACK = "#1a1a1a";
const BRAND_ORANGE = "#E8462A";

/** ELMETIC-Logo als Inline-SVG — „E" geometrisch mit orangem Mittelbalken (wie
 *  Original), Rest als fetter Schriftzug, GMBH vertikal, Claim mit orangen Punkten. */
function ElmeticLogo({ width = 210 }: { width?: number }) {
  return (
    <svg width={width} viewBox="0 0 256 70" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="ELMETIC GmbH — anspruchsvoll, vernetzt, sicher">
      {/* E aus Rechtecken: Stamm + 3 Arme, Mittelarm orange */}
      <rect x="2" y="11" width="9" height="39" fill={BRAND_BLACK} />
      <rect x="2" y="11" width="27" height="9.5" fill={BRAND_BLACK} />
      <rect x="2" y="25.5" width="21" height="9.5" fill={BRAND_ORANGE} />
      <rect x="2" y="40.5" width="27" height="9.5" fill={BRAND_BLACK} />
      {/* restlicher Schriftzug */}
      <text x="33" y="50" fontFamily="Arial, Helvetica, sans-serif" fontWeight="900" fontSize="54" letterSpacing="-3" fill={BRAND_BLACK}>LMETIC</text>
      {/* GMBH vertikal */}
      {["G", "M", "B", "H"].map((c, i) => (
        <text key={c} x="234" y={17 + i * 11} fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontSize="10" letterSpacing="1" fill={BRAND_BLACK}>{c}</text>
      ))}
      {/* Claim mit orangen Punkten */}
      <text x="3" y="66" fontFamily="Arial, Helvetica, sans-serif" fontWeight="600" fontSize="14.5" letterSpacing="0.2" fill={BRAND_BLACK}>
        anspruchsvoll <tspan fill={BRAND_ORANGE}>•</tspan> vernetzt <tspan fill={BRAND_ORANGE}>•</tspan> sicher
      </text>
    </svg>
  );
}

const UK_LABELS: Record<string, string> = {
  two_rail: "2-Schienen (Standard Ziegeldach)",
  cross_rail: "Kreuzverbund (lattenunabhängig)",
  three_rail: "3-Schienen (hohe Schneelast)",
  standing_seam: "Stehfalz / Blechfalzdach (Klemmmontage)",
};

const PRINT_CSS = `
  @media print {
    body * { visibility: hidden; }
    #cost-print-overlay, #cost-print-overlay * { visibility: visible; }
    #cost-print-overlay {
      position: absolute !important;
      left: 0;
      top: 0;
      width: 100%;
      background: white !important;
      padding: 0;
      margin: 0;
      overflow: visible !important;
    }
    .cost-print-no-print { display: none !important; visibility: hidden !important; }
  }
  @page { size: A4 portrait; margin: 15mm; }
`;

export function CostPrintSheet({
  result, kwp, moduleWp, ukSystem, projectName, customerName, onClose,
}: CostPrintSheetProps) {
  const out = result.data;
  const today = new Date().toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" });

  return (
    <>
      <style>{PRINT_CSS}</style>
      <div
        id="cost-print-overlay"
        style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.6)", overflowY: "auto" }}
        data-testid="cost-print-overlay"
      >
        {/* Steuerleiste — wird beim Drucken ausgeblendet */}
        <div
          className="cost-print-no-print"
          style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "8px 16px", background: "rgba(0,0,0,0.35)", position: "sticky", top: 0, zIndex: 10 }}
        >
          <span className="cost-print-no-print" style={{ alignSelf: "center", marginRight: "auto", fontSize: 11, color: "#e5e7eb" }}>
            Tipp: im Dialog als Ziel <strong>„Als PDF speichern"</strong> wählen.
          </span>
          <button
            onClick={() => window.print()}
            style={{ padding: "8px 18px", fontSize: 13, fontWeight: 600, background: BRAND_BLACK, color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}
          >
            📄 Als PDF speichern / Drucken (A4)
          </button>
          <button
            onClick={onClose}
            data-testid="cost-print-close"
            style={{ padding: "8px 14px", fontSize: 13, background: "#fff", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer" }}
          >
            Schliessen
          </button>
        </div>

        {/* A4-Blatt */}
        <div
          style={{
            background: "white",
            maxWidth: 794,
            margin: "12px auto 40px",
            padding: "16mm 14mm",
            fontFamily: "Arial, Helvetica, sans-serif",
            fontSize: 11,
            color: "#1a1a1a",
            boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
          }}
          data-testid="cost-print-sheet"
        >
          {/* ── Kopfzeile ── */}
          <div style={{ borderBottom: `3px solid ${BRAND_BLACK}`, paddingBottom: 12, marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: BRAND_BLACK, letterSpacing: -0.5 }}>
                PV-Richtwert-Kalkulation
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                Unverbindliche Kostenschätzung &mdash; ENTWURF &mdash; Kein Angebot
              </div>
              <div style={{ width: 64, height: 3, background: BRAND_ORANGE, marginTop: 6, borderRadius: 2 }} />
            </div>
            <div style={{ textAlign: "right" }}>
              <ElmeticLogo width={200} />
              <div style={{ fontSize: 9.5, color: "#6b7280", marginTop: 2 }}>
                Elektrotechnik &amp; Installation &middot; Tirol &middot; Kärnten
              </div>
            </div>
          </div>

          {/* ── Meta-Zeile ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14, fontSize: 10 }}>
            <div>
              {(customerName || projectName) && (
                <table style={{ borderCollapse: "collapse" }}>
                  <tbody>
                    {customerName && (
                      <tr>
                        <td style={{ color: "#6b7280", paddingRight: 8, paddingBottom: 2 }}>Kunde:</td>
                        <td style={{ fontWeight: 600 }}>{customerName}</td>
                      </tr>
                    )}
                    {projectName && (
                      <tr>
                        <td style={{ color: "#6b7280", paddingRight: 8 }}>Projekt:</td>
                        <td>{projectName}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <div>Erstellt: <strong>{today}</strong></div>
              <div style={{ color: "#b91c1c", marginTop: 2 }}>Richtwert &mdash; keine Gültigkeitsdauer</div>
            </div>
          </div>

          {/* ── Leerer Zustand ── */}
          {!out && (
            <div
              style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: 16, color: "#7f1d1d", textAlign: "center", marginBottom: 14 }}
              data-testid="cost-print-empty"
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Keine Kalkulation vorhanden</div>
              <div>Bitte zuerst eine Kalkulation berechnen und dann Druckblatt öffnen.</div>
            </div>
          )}

          {out && (
            <>
              {/* ── Technische Übersicht ── */}
              <div style={{ background: "#f6f6f5", borderRadius: 6, padding: "8px 12px", marginBottom: 14, borderLeft: `3px solid ${BRAND_BLACK}` }}>
                <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12 }}>Anlage</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "3px 16px" }}>
                  <div>Anlagengrösse: <strong>{kwp.toLocaleString("de-AT")} kWp</strong></div>
                  <div>Module: <strong>{out.quantities.moduleCount} Stk &times; {moduleWp} Wp</strong></div>
                  <div>Montage: <strong>{UK_LABELS[ukSystem] ?? ukSystem}</strong></div>
                  <div>Schienenmeter: <strong>{out.quantities.railMeters} m</strong></div>
                  <div>Montagezeit: <strong>{out.quantities.labourHours} h</strong></div>
                  <div>DC-Schutz (ÜSS): <strong>{out.quantities.dcSurgeCount} Stk</strong></div>
                </div>
              </div>

              {/* ── Kostentabelle ── */}
              <div style={{ fontWeight: 700, marginBottom: 5, fontSize: 12, borderBottom: "1px solid #d1d5db", paddingBottom: 3 }}>
                Kostenpositionen (Richtwerte, netto exkl. USt)
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 14 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #d1d5db", color: "#6b7280" }}>
                    <th style={{ textAlign: "left", padding: "3px 0", fontWeight: 400 }}>Position</th>
                    <th style={{ textAlign: "right", padding: "3px 0", width: 110, fontWeight: 400 }}>Richtwert netto</th>
                  </tr>
                </thead>
                <tbody>
                  {out.materialPositions.map((p) => (
                    <tr key={p.key} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "3px 0" }}>{p.label}</td>
                      <td style={{ textAlign: "right", padding: "3px 0" }}>{eur(p.vkEur)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "3px 0" }}>{out.labourPosition.label}</td>
                    <td style={{ textAlign: "right", padding: "3px 0" }}>{eur(out.labourPosition.vkEur)}</td>
                  </tr>
                  {out.flatPositions.map((p) => (
                    <tr key={p.key} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "3px 0" }}>{p.label}</td>
                      <td style={{ textAlign: "right", padding: "3px 0" }}>{eur(p.vkEur)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "1px solid #d1d5db", fontWeight: 600 }}>
                    <td style={{ padding: "4px 0" }}>Zwischensumme netto</td>
                    <td style={{ textAlign: "right", padding: "4px 0" }}>{eur(out.subtotalNetEur)}</td>
                  </tr>
                  {out.surchargeEur !== 0 && (
                    <tr>
                      <td style={{ padding: "3px 0" }}>+ Gesamtaufschlag</td>
                      <td style={{ textAlign: "right", padding: "3px 0" }}>{eur(out.surchargeEur)}</td>
                    </tr>
                  )}
                  <tr style={{ borderTop: `2px solid ${BRAND_BLACK}`, fontWeight: 700, fontSize: 13 }}>
                    <td style={{ padding: "5px 0" }}>RICHTWERT GESAMT netto</td>
                    <td style={{ textAlign: "right", padding: "5px 0" }} data-testid="cost-print-total-net">{eur(out.totalNetEur)}</td>
                  </tr>
                  <tr style={{ color: "#6b7280" }}>
                    <td style={{ padding: "3px 0" }}>+ 20&nbsp;% USt</td>
                    <td style={{ textAlign: "right", padding: "3px 0" }}>{eur(out.vatEur)}</td>
                  </tr>
                  <tr style={{ fontWeight: 700, fontSize: 13, background: "#fbe9e4", borderTop: `2px solid ${BRAND_ORANGE}` }}>
                    <td style={{ padding: "6px 8px", color: BRAND_BLACK }}>RICHTWERT GESAMT brutto</td>
                    <td style={{ textAlign: "right", padding: "6px 8px", color: BRAND_BLACK }} data-testid="cost-print-total-gross">{eur(out.totalGrossEur)}</td>
                  </tr>
                </tbody>
              </table>

              {/* ── Kennzahlen ── */}
              <div style={{ display: "flex", gap: 24, fontSize: 10, marginBottom: 14, padding: "5px 8px", background: "#f6f6f5", borderRadius: 4, borderLeft: `3px solid ${BRAND_ORANGE}` }}>
                <div>Richtwert €/kWp netto: <strong style={{ color: BRAND_ORANGE }}>{eur(out.pricePerKwpNetEur)}</strong></div>
                <div style={{ color: "#6b7280" }}>Sätze: ELMETIC-Preisvergleich 06/2026</div>
              </div>

              {/* ── Warnungen (nur severity=warning) ── */}
              {result.warnings.filter((w) => w.severity === "warning").length > 0 && (
                <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 4, padding: "6px 8px", fontSize: 10, color: "#92400e", marginBottom: 14 }}>
                  {result.warnings
                    .filter((w) => w.severity === "warning")
                    .map((w, i) => <div key={i}>⚠ {w.message}</div>)}
                </div>
              )}
            </>
          )}

          {/* ── Haftungshinweis (immer sichtbar) ── */}
          <div
            style={{ marginTop: 20, border: "2px solid #b91c1c", borderRadius: 6, padding: "10px 14px", background: "#fef2f2", fontSize: 9.5, color: "#7f1d1d", lineHeight: 1.6 }}
            data-testid="cost-print-disclaimer"
          >
            <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 11 }}>WICHTIGER HINWEIS &mdash; KEIN ANGEBOT</div>
            {PRINT_DISCLAIMER}
          </div>

          {/* ── Fusszeile ── */}
          <div style={{ marginTop: 18, borderTop: "1px solid #d1d5db", paddingTop: 8, fontSize: 9, color: "#9ca3af", display: "flex", justifyContent: "space-between" }}>
            <div>ELMETIC Elektrotechnik &amp; Installation &middot; Tirol &amp; Kärnten</div>
            <div>EnergyOS Planungssystem &middot; Interner Kalkulations-Entwurf (canApprove: false)</div>
          </div>
        </div>
      </div>
    </>
  );
}
